/**
 * Schema-neutral type definitions shared by every questionnaire schema (AI
 * intake, company-profile baseline, WAFR / FTR modules). `intakeSchema.ts`
 * re-exports them.
 */

export type FieldType =
  | { kind: 'text'; placeholder?: string }
  | { kind: 'textarea'; rows?: number; placeholder?: string }
  | { kind: 'number'; placeholder?: string; min?: number; max?: number }
  | { kind: 'radio'; options: ChoiceOption[] }
  | { kind: 'select'; options: ChoiceOption[]; placeholder?: string }
  | { kind: 'multiselect'; options: ChoiceOption[]; allowOther?: boolean }
  | { kind: 'list'; placeholder?: string }
  | { kind: 'eligibility-grid'; rows: ChoiceOption[] }
  | { kind: 'use-cases-table' }
  | { kind: 'qa-list' }
  | { kind: 'contacts-table' }

export interface ChoiceOption {
  value: string
  label: string
  hint?: string
  /** Optional long-form help body shown in the modal alongside the short hint. */
  help?: string
}

export interface FieldDef {
  key: string
  label: string
  /** One-paragraph summary. Shown at the top of the help modal as a quick read. */
  hint: string
  /** Optional long-form, beginner-friendly markdown explanation (paragraphs,
   *  `- bullets`, `**bold**`, `` `code` ``, `## headings`). */
  help?: string
  type: FieldType
  /** Priority signal from the AWS program guides: `required` — AWS rejects the
   *  Fund Request (or the claim) without it; `recommended` — strongly advised,
   *  the deterministic renderers depend on it for useful output. */
  priority?: 'required' | 'recommended'
  required?: boolean
  /** Render at half width: consecutive `half` fields pair up two per line
   *  (e.g. HQ city + state, postal + country). Ignored by the two-column
   *  form layout, which already grids everything. */
  half?: boolean
}

/** Group a field list into render rows: consecutive `half` fields pair up
 *  two per row; everything else gets its own full-width row. Shared by the
 *  single-column form renderers (SectionFieldsForm, the public onboarding
 *  page) so paired fields lay out identically everywhere. */
export function fieldRows(fields: FieldDef[]): FieldDef[][] {
  const rows: FieldDef[][] = []
  for (let i = 0; i < fields.length; i++) {
    const f = fields[i]!
    const next = fields[i + 1]
    if (f.half && next?.half) {
      rows.push([f, next])
      i++
    } else rows.push([f])
  }
  return rows
}

/** Which phase of the AI-intake workflow this section is collected in.
 *  - `company` — company basics, edited on the Company profile form (never as
 *    an intake section), though its required fields still gate intake finalize.
 *  - `intake` — the intake page.
 *  - `proposals` — the use-case hypotheses, on the Use cases stage page.
 *  - `engagement` — the funding paperwork, on Submission's ACE & Funding tab. */
export type Phase = 'company' | 'intake' | 'proposals' | 'engagement'

export interface SectionDef {
  key: string
  number: number
  title: string
  /** Short label for the section sidebar so it renders on one line; the form
   *  pane heading always shows the full `title`. Falls back to `title`. */
  navTitle?: string
  description?: string
  /** Longer beginner-friendly explanation of what the whole section is for,
   *  rendered as markdown in a help modal beside the section title. */
  help?: string
  fields: FieldDef[]
  /** AI-intake workflow phase. Only the AI-intake sections participate in the
   *  intake workflow — company-profile and WAFR/FTR module sections omit it. */
  phase?: Phase
  /** Where the section's values are stored.
   *  - `company` — on the shared company profile (read/written by every module).
   *  - `module` (or absent) — on the owning module's own instance. */
  target?: 'company' | 'module'
}
